// Modal de bienvenida del Editor de Video (primera vez), en la pantalla de
// proyectos (/editor-video). Mismo look que el modal de novedades de la web
// (UpdateInfoModal): desktop con texto a la izquierda y un video a la derecha.
// NO reusa UpdateInfoModal (ese es para los anuncios de la web) — es propio.
//
// El botón principal es "Crear proyecto" (no "Entendido"): crea un proyecto
// con la marca actual del cliente y entra al editor (lo maneja el caller).
//
// El "mostrar una sola vez" lo maneja el caller (VideoProjects) con un flag de
// localStorage. Este componente es tonto: recibe open/creating y avisa
// onCreate / onSkip.

import React from "react";
import {
  X,
  Sparkles,
  Clapperboard,
  Scissors,
  Palette,
  Download,
  Play,
  Loader2,
} from "lucide-react";

// Video de bienvenida, servido desde Supabase storage (bucket público
// `system`, igual que los videos de novedades) — así no infla el repo/bundle.
const WELCOME_VIDEO_URL: string | null =
  "https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/home_message/20junio/welcome-editor.mp4";

interface WelcomeEditorModalProps {
  open: boolean;
  /** true mientras se crea el proyecto (spinner en el botón). */
  creating?: boolean;
  /** "Crear proyecto" → crea el proyecto y entra al editor. */
  onCreate: () => void;
  /** "Saltar" / cerrar → no crea nada, queda en la lista de proyectos. */
  onSkip: () => void;
}

const FEATURES: Array<{
  Icon: React.ComponentType<{ className?: string }>;
  text: string;
}> = [
  { Icon: Clapperboard, text: "El Director arma tu video: pedile lo que quieras." },
  { Icon: Sparkles, text: "Generá escenas, imágenes y diálogos con IA." },
  {
    Icon: Scissors,
    text: "Editá con IA: sacá silencios y cortá videos, todo desde el Director.",
  },
  { Icon: Palette, text: "Tu marca aplicada: logo, colores y tipografías." },
  { Icon: Download, text: "Exportá tu video listo para publicar." },
];

export const WelcomeEditorModal: React.FC<WelcomeEditorModalProps> = ({
  open,
  creating = false,
  onCreate,
  onSkip,
}) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div
        className="bg-white rounded-lg shadow-[0_8px_40px_rgba(0,0,0,0.12)] relative flex overflow-hidden w-[min(360px,calc(100vw-32px))] sm:w-[1050px] sm:max-w-[90vw]"
        style={{ height: "min(560px, 85vh)" }}
      >
        {/* Cerrar / Saltar */}
        <button
          type="button"
          onClick={onSkip}
          aria-label="Saltar"
          className="absolute top-3 right-3 w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors z-10"
        >
          <X className="w-4 h-4" />
        </button>

        {/* ── Izquierda: texto + acciones ── */}
        <div className="flex-1 flex flex-col px-9 py-8 min-w-0">
          <div className="flex-1 flex flex-col justify-center">
            <h2 className="text-[24px] font-semibold text-brand-black/90 leading-snug">
              Bienvenido a Estudio de Video
            </h2>
            <p className="text-[15px] text-brand-black/50 mt-2 max-w-md">
              Creá videos publicitarios con IA, desde cero. Te damos una mano
              para arrancar — en un par de clicks ya estás editando.
            </p>

            <ul className="mt-6 space-y-3">
              {FEATURES.map(({ Icon, text }, i) => (
                <li key={i} className="flex items-center gap-3">
                  <span className="w-8 h-8 shrink-0 rounded-lg bg-brand-primary/10 flex items-center justify-center">
                    <Icon className="w-4 h-4 text-brand-primary" />
                  </span>
                  <span className="text-[14px] text-brand-black/70 leading-snug">
                    {text}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Acciones */}
          <div className="mt-6 flex items-center gap-3">
            <button
              type="button"
              onClick={onCreate}
              disabled={creating}
              className="inline-flex items-center justify-center gap-2 h-[44px] px-7 rounded-md bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium shadow-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {creating ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4" />
              )}
              Crear proyecto
            </button>
            <button
              type="button"
              onClick={onSkip}
              disabled={creating}
              className="h-[44px] px-3 text-[14px] font-medium text-brand-black/55 hover:text-brand-black/90 transition-colors disabled:opacity-40"
            >
              Saltar
            </button>
          </div>
        </div>

        {/* ── Derecha: video (o placeholder hasta que cargues el mp4) ── */}
        <div className="hidden sm:block shrink-0 w-[52%] relative overflow-hidden bg-neutral-100">
          {WELCOME_VIDEO_URL ? (
            <video
              src={WELCOME_VIDEO_URL}
              autoPlay
              muted
              loop
              playsInline
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : (
            // Placeholder elegante con gradiente de marca + play.
            <div
              className="absolute inset-0 flex flex-col items-center justify-center"
              style={{
                background:
                  "linear-gradient(135deg, var(--brand-primary) 0%, var(--brand-deep) 60%, var(--brand-black) 100%)",
              }}
            >
              <span className="w-16 h-16 rounded-full bg-white/15 backdrop-blur-sm flex items-center justify-center shadow-lg">
                <Play className="w-7 h-7 text-white translate-x-[2px]" />
              </span>
              <span className="mt-4 text-[13px] font-medium text-white/80 uppercase tracking-wide">
                Video próximamente
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WelcomeEditorModal;
